import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check, Paintbrush, Home as HomeIcon, Sparkles, Ruler, PartyPopper, Building2, Package, Palette, Droplets, Shield, Brush, Info, Quote, Star, X, Maximize2, ShoppingBag } from "lucide-react";
import PaymentBadges from "@/components/PaymentBadges";
import { publicApi } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { trackProductClick } from "@/lib/analytics";

const AD_HERO = "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/z1ql434x_165705cc-c984-4126-af09-4f54322fa52c.jpg";

const INCLUDED = [
  "Required paints",
  "Brushes (rollers if required)",
  "Protective sheets under painting",
  "Paint containers",
  "Paint mixing procedures",
  "Finishing sealers (if needed)",
];

const SUITABLE = [
  { icon: HomeIcon, label: "Private homes" },
  { icon: Building2, label: "Workplaces" },
  { icon: Sparkles, label: "Indoor & outdoor" },
  { icon: PartyPopper, label: "Party options" },
  { icon: Ruler, label: "From 1m × 1m" },
  { icon: Paintbrush, label: "Any surface" },
];

const CAM_TIERS = [
  { name: "Small feature wall", size: "up to 6 m²", price: "from $850" },
  { name: "Medium mural", size: "6 – 20 m²", price: "$1,800 – $4,500" },
  { name: "Large exterior mural", size: "20 – 60 m²", price: "$4,500 – $12,000" },
  { name: "Signature scale project", size: "60 m²+", price: "Quote on request" },
];

const DIY_TIERS_UNUSED = []; // kept for backwards-compat; tiers now come from the DB via publicApi.listProducts({category: "diy_kits"})

const KIT_CONTENTS = [
  { icon: Palette, title: "Required paints", desc: "Pre-mixed to your chosen colours — enough for the whole piece." },
  { icon: Brush, title: "Brushes (+ rollers)", desc: "The right kit for your surface — fine detail brushes through to rollers where needed." },
  { icon: Shield, title: "Protective sheets", desc: "Drop sheets & masking to place under and around your painting area." },
  { icon: Package, title: "Paint containers", desc: "Sealable containers so you can pause and pick back up without waste." },
  { icon: Droplets, title: "Paint mixing procedures", desc: "Ratios and step-by-step so your colours stay true from start to finish." },
  { icon: Sparkles, title: "Finishing sealers (if needed)", desc: "Clear protective coat for outdoor or high-traffic walls — applied by us or supplied to you." },
  { icon: Paintbrush, title: "Custom stencil, applied", desc: "Your artwork cut and applied to the wall so you paint by the lines." },
  { icon: Info, title: "Step-by-step guidance", desc: "Written how-to and on-call phone support from Cam while you paint." },
];

const CAM_MURALS = [
  {
    img: "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/3kzuj1un_Untitledfxfcftcgc.png",
    title: "Bunjil's Watch",
    subtitle: "Watching his watering hole · poolside",
    size: "~18 m² · Melbourne, VIC",
    price: "$6,500",
    tag: "Cam paints",
    tall: true,
  },
  {
    img: "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/dip6komw_Copilot_20260426_092800.png",
    title: "Aboriginal Eagle",
    subtitle: "Front fence panel · exterior",
    size: "24 m² · dot work + red-earth palette",
    price: "$8,000",
    tag: "Cam paints",
  },
  {
    img: "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/vud4u3ud_Screenshot_20260409_124751_Adobe%20Photoshop%20Express%281%29.jpg",
    title: "Frog Dreamscape",
    subtitle: "Garage door mural · exterior",
    size: "~6 m² · roller door",
    price: "$2,500",
    tag: "Cam paints",
  },
];

const DIY_MURALS = [
  {
    img: "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/k7jztfcd_image.png",
    title: "Beauty and Beyond",
    subtitle: "Bedroom feature wall · painted by client",
    size: "~4 m² · interior · Standard kit",
    price: "$650 kit",
    tag: "Paint-your-own",
  },
];

const REVIEWS = [
  {
    quote: "The team at CKA were amazing. I still can't believe I painted this.",
    name: "Paint-your-own client",
    context: "Guided kit · Ballarat, VIC",
    stars: 5,
  },
  {
    quote: "Cam transformed our backyard wall into something we're proud to show off. The birds, the sunrise, the colours — every visitor asks about it.",
    name: "The Nguyen family",
    context: "Backyard feature wall · Melbourne",
    stars: 5,
  },
  {
    quote: "Kids' bedroom mural is the star of our home. Cam listened to the story we wanted told and delivered it beautifully.",
    name: "Rachel M.",
    context: "Interior mural · Geelong",
    stars: 5,
  },
  {
    quote: "Absolutely incredible work. Professional, on-time, and the design brief nailed on the first go. Highly recommend for any custom mural.",
    name: "Bayside Café",
    context: "Commercial exterior · Frankston",
    stars: 5,
  },
];

export default function MuralService() {
  const [lightbox, setLightbox] = useState(null);
  const [kits, setKits] = useState([]);
  const { addItem } = useCart();
  const navigate = useNavigate();
  const openLightbox = (item) => setLightbox(item);
  const closeLightbox = () => setLightbox(null);

  useEffect(() => {
    publicApi.listProducts({ category: "diy_kits" })
      .then((list) => setKits([...list].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))))
      .catch(() => {});
  }, []);

  const addKitToCart = (product) => {
    addItem({
      id: product.id,
      title: product.title,
      price: product.price,
      qty: 1,
      image_url: product.image_url,
    });
    trackProductClick(product.id, "diy_add_to_cart");
    toast.success("Added to cart");
  };

  const goToKit = (product) => {
    trackProductClick(product.id, "diy_view");
    navigate(`/shop/${product.id}`);
  };

  return (
    <div data-testid="page-mural-service" className="text-white">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={AD_HERO} alt="Paint your own mural — CKA" className="w-full h-full object-cover opacity-[0.12] blur-sm" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/95 via-[#0A0A0A]/90 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-24 pb-20">
          <p className="overline mb-5" data-testid="mural-eyebrow">The Studio · Murals</p>
          <h1 data-testid="mural-title" className="font-display text-5xl sm:text-7xl md:text-[9rem] leading-[0.85] max-w-5xl">
            Paint your <br />
            <span className="text-[#FF4500]">own mural</span>. <br />
            Or let Cam paint it.
          </h1>
          <p className="mt-8 max-w-2xl text-white/70 text-lg leading-relaxed">
            Bring your art & ideas to life. We supply everything you need — from stencil design through to finishing sealers — so the hard part of mural art is done for you.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/custom?type=mural" data-testid="mural-cta-quote" className="btn-primary">
              Request a mural quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#gallery" className="btn-ghost">See recent murals</a>
            <a href="#diy" data-testid="mural-cta-diy" className="btn-ghost">
              Paint-Your-Own kits →
            </a>
          </div>
        </div>
      </section>

      {/* CAM-PAINTED MURALS — real work */}
      <section id="gallery" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="overline mb-3">Cam-painted · Real walls · Real work</p>
            <h2 className="font-display text-4xl md:text-6xl">Recent murals.</h2>
          </div>
          <p className="text-white/50 text-sm max-w-md">Painted end-to-end by Cam. Prices below are the actual total charged for each project — every quote is bespoke.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {CAM_MURALS.map((g, i) => (
            <button
              key={i}
              type="button"
              data-testid={`mural-gallery-${i}`}
              onClick={() => openLightbox(g)}
              className={`group relative overflow-hidden bg-[#141414] border border-white/10 hover:border-[#FF4500] transition-colors text-left cursor-zoom-in ${g.tall ? "md:col-span-2 md:row-span-2" : ""}`}
            >
              <div className={`overflow-hidden ${g.tall ? "aspect-[4/3] md:aspect-auto md:h-full" : "aspect-square"}`}>
                <img
                  src={g.img}
                  alt={g.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="absolute top-3 left-3 bg-[#0A0A0A]/70 backdrop-blur border border-white/20 text-white/90 text-[10px] uppercase tracking-widest px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                <Maximize2 className="w-3 h-3" /> Click to enlarge
              </div>
              <div className="absolute top-3 right-3 bg-[#FF4500] text-white text-xs uppercase tracking-widest px-2.5 py-1 font-semibold shadow-lg">
                {g.price}
              </div>
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent pt-16">
                <p className="font-display text-lg md:text-xl tracking-wider leading-tight">{g.title}</p>
                <p className="text-xs text-white/70 mt-1">{g.subtitle}</p>
                <p className="text-[10px] text-white/40 uppercase tracking-widest mt-2">{g.size}</p>
                <p className="text-[10px] text-[#FF4500] uppercase tracking-widest mt-1 font-semibold">Full cost of completed mural</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          data-testid="mural-lightbox"
          className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-in fade-in duration-200"
          onClick={closeLightbox}
        >
          <button
            data-testid="mural-lightbox-close"
            onClick={closeLightbox}
            className="absolute top-4 right-4 md:top-8 md:right-8 w-11 h-11 border border-white/20 bg-[#0A0A0A]/60 flex items-center justify-center hover:border-[#FF4500] hover:text-[#FF4500] z-10"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="relative max-w-[92vw] max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.img}
              alt={lightbox.title}
              className="max-w-full max-h-[78vh] object-contain mx-auto"
            />
            <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3 text-white">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{lightbox.tag}</p>
                <p className="font-display text-2xl md:text-3xl tracking-wider">{lightbox.title}</p>
                <p className="text-sm text-white/70 mt-1">{lightbox.subtitle}</p>
                <p className="text-[11px] uppercase tracking-widest text-white/40 mt-1">{lightbox.size}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-[#FF4500] font-semibold">Full cost of completed mural</p>
                <p className="font-display text-3xl md:text-4xl text-[#FF4500]">{lightbox.price}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PITCH */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-16 items-start">
        <div>
          <p className="overline mb-3">The offering</p>
          <h2 className="font-display text-4xl md:text-6xl mb-8">"I wish I could do that." <span className="text-[#FF4500]">You can.</span></h2>
          <div className="space-y-5 text-white/70 leading-relaxed">
            <p>
              Do you ever look at murals and think <em>"I wish I could do that"</em> — but for whatever reason you can't? Here at Cam's Koori Art we change that for you.
            </p>
            <p>
              We may not turn you into a creative genius overnight, but we can take the artwork you've chosen — whether it's something you've drawn yourself or a piece we've created around your ideas — and do the hard parts of mural art for you.
            </p>
            <p>
              We work to <strong className="text-white">any scale and dimensions</strong> — a stencil we apply to your chosen area, a feature wall (interior or exterior), and on almost any surface.
            </p>
          </div>

          {/* TESTIMONIAL */}
          <blockquote className="mt-10 border-l-2 border-[#FF4500] pl-6">
            <p className="font-display text-2xl md:text-3xl text-white leading-tight">
              "The team at CKA were amazing. I still can't believe I painted this."
            </p>
            <p className="text-xs uppercase tracking-widest text-white/40 mt-3">Paint-your-own client · CKA archive</p>
          </blockquote>
        </div>

        <div className="grid grid-cols-1 gap-6">
          <div className="border border-white/10 bg-[#141414] p-6">
            <p className="overline mb-4">What we leave you with</p>
            <ul className="space-y-3">
              {INCLUDED.map((it) => (
                <li key={it} data-testid={`mural-included-${it.split(" ")[0].toLowerCase()}`} className="flex items-start gap-3 text-sm">
                  <Check className="w-4 h-4 text-[#FF4500] mt-0.5 shrink-0" />
                  <span className="text-white/80">{it}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-white/40 mt-5 pt-4 border-t border-white/10">
              You get everything you need to complete the mural — start to finish.
            </p>
          </div>

          <div className="border border-white/10 bg-[#141414] p-6">
            <p className="overline mb-4">Suitable for</p>
            <div className="grid grid-cols-2 gap-3">
              {SUITABLE.map((s) => (
                <div key={s.label} className="flex items-center gap-2.5 text-sm text-white/80">
                  <s.icon className="w-4 h-4 text-[#FF4500] shrink-0" />
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CAM-PAINTED PRICING */}
      <section id="pricing" className="border-y border-white/10 bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
          <div className="max-w-2xl mb-12">
            <p className="overline mb-3">Cam-painted mural pricing</p>
            <h2 className="font-display text-4xl md:text-6xl mb-4">Hand the whole thing to Cam.</h2>
            <p className="text-white/60">Bespoke design + full application, by Cam. Ranges below are indicative — every wall gets its own quote.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CAM_TIERS.map((t, i) => (
              <div key={t.name} data-testid={`mural-tier-${i}`} className="border border-white/10 bg-[#141414] p-6 hover:border-[#FF4500] transition-colors">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <h3 className="font-display text-xl tracking-wider">{t.name}</h3>
                  <p className="text-[#FF4500] font-semibold whitespace-nowrap">{t.price}</p>
                </div>
                <p className="text-[10px] uppercase tracking-widest text-white/40">{t.size}</p>
                {t.note && <p className="text-xs text-white/60 mt-3">{t.note}</p>}
              </div>
            ))}
          </div>
          <p className="text-xs text-white/40 mt-8 max-w-2xl">
            Quotes include design, primer, and clear-coat protection where applicable. Travel outside Vic/NSW additional. Every project is unique — request a proper quote and Cam will get back within 2 business days.
          </p>
        </div>
      </section>

      {/* ─── PAINT-YOUR-OWN KITS (DIY) — DEDICATED SECTION ─── */}
      <section id="diy" className="bg-gradient-to-b from-[#141414] via-[#0F0F0F] to-[#0A0A0A] border-b border-white/10">
        {/* DIY intro banner */}
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-24 pb-12">
          <div className="inline-flex items-center gap-2 border border-[#FF4500]/50 bg-[#FF4500]/[0.08] text-[#FF4500] text-[10px] uppercase tracking-widest px-3 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 bg-[#FF4500] rounded-full animate-pulse" /> Do-It-Yourself · Paint-Your-Own Kits
          </div>
          <h2 className="font-display text-5xl md:text-8xl leading-[0.9] mb-6 max-w-4xl">
            Paint it <br /><span className="text-[#FF4500]">yourself.</span>
          </h2>
          <p className="text-white/70 max-w-2xl text-lg leading-relaxed">
            A separate offering — no Cam on-site, no big commission. Just you, a wall, and a kit that has everything you need. Ship-to-your-door across AU.
          </p>
        </div>

        {/* What's in the kit */}
        <div id="kit" className="max-w-[1400px] mx-auto px-5 md:px-10 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12 items-start">
            <div>
              <p className="overline mb-3">What&rsquo;s in the box</p>
              <h3 className="font-display text-3xl md:text-5xl mb-6">Everything you need.</h3>
              <p className="text-white/70 leading-relaxed mb-4">
                Every DIY kit comes with <strong className="text-white">everything you need to complete the mural</strong> — start to finish. No trips to the paint shop, no guessing at ratios, no scrambling for drop sheets.
              </p>
              <p className="text-white/70 leading-relaxed">
                Cam preps the artwork, cuts the stencil, mixes the paints, and ships you a kit that&rsquo;s ready to roll. You bring the wall &mdash; and the story.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {KIT_CONTENTS.map((k, i) => (
                <div
                  key={k.title}
                  data-testid={`kit-item-${i}`}
                  className="border border-white/10 bg-[#141414] p-5 hover:border-[#FF4500]/60 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 border border-[#FF4500]/40 bg-[#FF4500]/[0.08] flex items-center justify-center shrink-0">
                      <k.icon className="w-4 h-4 text-[#FF4500]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-display text-base tracking-wider leading-tight">{k.title}</p>
                      <p className="text-xs text-white/60 mt-1.5 leading-relaxed">{k.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* DIY Pricing — real shop products with Add-to-Cart */}
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12">
          <div className="max-w-2xl mb-10">
            <p className="overline mb-3">DIY kit pricing</p>
            <h3 className="font-display text-3xl md:text-5xl mb-4">Pick a kit by wall size.</h3>
            <p className="text-white/60">All kits ship Australia-wide. Prices scale with wall size and complexity — outdoor sealer included on XL kits at no extra cost. Add to cart and pay by PayID or PayPal.</p>
          </div>
          {kits.length === 0 ? (
            <p className="text-white/50 text-sm">Kits loading…</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {kits.map((k, i) => {
                const isFeatured = k.is_featured || i === 1;
                return (
                  <div
                    key={k.id}
                    data-testid={`diy-tier-${i}`}
                    className={`relative border ${isFeatured ? "border-[#FF4500]" : "border-white/10"} bg-[#141414] p-6 hover:border-[#FF4500] transition-colors flex flex-col`}
                  >
                    {isFeatured && (
                      <div className="absolute -top-3 left-6 bg-[#FF4500] text-white text-[10px] uppercase tracking-widest px-2 py-1 font-semibold">
                        Most popular
                      </div>
                    )}
                    <p className="text-[10px] uppercase tracking-widest text-[#FF4500] font-semibold mb-3">DIY Kit</p>
                    <h4 className="font-display text-xl tracking-wider mb-2 leading-tight">
                      {k.title.replace(/^Paint-Your-Own · /, "")}
                    </h4>
                    <p className="text-[10px] uppercase tracking-widest text-white/40 mb-4">{k.kit_size || "Custom size"}</p>
                    <p className="font-display text-3xl text-[#FF4500] mb-4">${k.price.toFixed(0)}</p>
                    <p className="text-xs text-white/60 leading-relaxed flex-1 mb-5">
                      {k.description.split(". ").slice(0, 2).join(". ") + "."}
                    </p>
                    <div className="flex flex-col gap-2">
                      <button
                        data-testid={`diy-add-cart-${i}`}
                        onClick={() => addKitToCart(k)}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#FF4500] text-white py-3 text-xs uppercase tracking-widest font-semibold hover:bg-[#FF5A15] transition-colors"
                      >
                        <ShoppingBag className="w-3.5 h-3.5" /> Add to cart
                      </button>
                      <button
                        data-testid={`diy-view-${i}`}
                        onClick={() => goToKit(k)}
                        className="w-full text-center py-2 text-[10px] uppercase tracking-widest text-white/70 border border-white/15 hover:border-white/40 hover:text-white"
                      >
                        View details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* DIY Gallery — clients who painted their own */}
        {DIY_MURALS.length > 0 && (
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12">
            <div className="mb-8">
              <p className="overline mb-3">DIY in the wild</p>
              <h3 className="font-display text-3xl md:text-5xl">Painted by clients.</h3>
              <p className="text-white/50 text-sm mt-2 max-w-xl">Real walls, painted by real people using our kits. Send yours in and we&rsquo;ll add it to the wall.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {DIY_MURALS.map((g, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`diy-gallery-${i}`}
                  onClick={() => openLightbox(g)}
                  className="group relative overflow-hidden bg-[#141414] border border-white/10 hover:border-[#FF4500] transition-colors text-left cursor-zoom-in aspect-square"
                >
                  <img src={g.img} alt={g.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute top-3 right-3 bg-[#FF4500] text-white text-xs uppercase tracking-widest px-2.5 py-1 font-semibold shadow-lg">{g.price}</div>
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent pt-16">
                    <p className="font-display text-lg md:text-xl tracking-wider leading-tight">{g.title}</p>
                    <p className="text-xs text-white/70 mt-1">{g.subtitle}</p>
                    <p className="text-[10px] text-white/40 uppercase tracking-widest mt-2">{g.size}</p>
                    <p className="text-[10px] text-[#FF4500] uppercase tracking-widest mt-1 font-semibold">Client-painted · DIY kit</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* DIY CTA */}
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 text-center">
          <Link to="/shop" data-testid="diy-cta" className="btn-primary inline-flex">
            See all kits in the shop <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-[11px] text-white/40 mt-4">Ships within 5 business days of design confirmation · pay by PayID (no fees) or PayPal.</p>
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="border-y border-white/10 bg-[#0F0F0F]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="overline mb-3">In their words</p>
              <h2 className="font-display text-4xl md:text-6xl">Reviews.</h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {[0,1,2,3,4].map((n) => (
                  <Star key={n} className="w-5 h-5 fill-[#FF4500] text-[#FF4500]" />
                ))}
              </div>
              <p className="text-white/60 text-sm"><span className="text-white font-semibold">5.0</span> average · CKA clients</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {REVIEWS.map((r, i) => (
              <blockquote
                key={i}
                data-testid={`mural-review-${i}`}
                className="relative border border-white/10 bg-[#141414] p-6 md:p-7 hover:border-[#FF4500]/60 transition-colors"
              >
                <Quote className="absolute top-5 right-5 w-8 h-8 text-[#FF4500]/20" />
                <div className="flex items-center gap-0.5 mb-4">
                  {Array.from({ length: r.stars }).map((_, n) => (
                    <Star key={n} className="w-4 h-4 fill-[#FF4500] text-[#FF4500]" />
                  ))}
                </div>
                <p className="text-white/85 leading-relaxed text-base md:text-lg">&ldquo;{r.quote}&rdquo;</p>
                <footer className="mt-5 pt-4 border-t border-white/10">
                  <p className="font-display text-sm tracking-wider">{r.name}</p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{r.context}</p>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* PAYMENT BADGES + CTA */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
        <div>
          <p className="overline mb-3">How to book</p>
          <h2 className="font-display text-4xl md:text-6xl mb-6">Start with a story.</h2>
          <p className="text-white/70 leading-relaxed mb-8 max-w-xl">
            Send us your idea (or a rough sketch), your wall size, and where you're based. We'll come back with a concept and a firm quote — then you pay a deposit via PayID or PayPal to lock in your dates.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/custom?type=mural" data-testid="mural-cta-request" className="btn-primary">
              Start a mural request <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-ghost">
              Ask Cam a question
            </Link>
          </div>
        </div>
        <PaymentBadges />
      </section>
    </div>
  );
}
